import { filterItemOffersByName } from "@/lib/filter-item-offers-by-name";
import { closeSearchAtom, searchTermAtom } from "@/atoms/search";
import { FloatingFullscreenButton } from "@/components";
import { FloatingCartButton } from "@/components/cart/FloatingCartButton";
import {
  CategoriesList,
  CategoriesNavigation,
  CategoryPickerModal,
  HeaderMenu,
  ItemOfferCard,
} from "@/components/home";
import { StorefrontSections } from "@/components/home/StorefrontSections";
import { DaypartSuggestionsRow } from "@/components/suggestions/DaypartSuggestionsRow";
import { CouponReveal } from "@/components/marketing/CouponReveal";
import { SchedulingBanner } from "@/components/scheduling/SchedulingBanner";
import { ItemModalContent } from "@/components/item/ItemModalContent";
import { ItemModal } from "@/components/modals/ItemModal";
import { useCategoryDeepLink } from "@/hooks/use-category-deep-link";
import { useCategoryScrollSpy } from "@/hooks/use-category-scroll-spy";
import { useBrowsingChannelCatalog } from "@/hooks/use-browsing-channel-catalog";
import { useOptimisticCart } from "@/hooks/use-optimistic-cart";
import { usePageBackground } from "@/hooks/use-page-background";
import { useItemModalParam } from "@/hooks/use-item-modal-param";
import { useMerchantStatus } from "@/hooks/use-merchant-status";
import LottieComponent from "@/components/lottie-animations/LottieComponent";
import { BasicInfo, Service } from "@/types";
import { SquaresFour } from "@/icons";
import { cn } from "@/lib/cn";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { EmptyState, IconButton, PageContainer, SearchEmpty, Surface } from "@/components/ui";

export function ListMenu({
  basicInfo,
  itemNames,
  deliveryService,
}: {
  basicInfo: BasicInfo;
  itemNames: Record<string, string> | undefined;
  deliveryService: Service;
}) {
  const {
    visibleItemOffers,
    categoriesItemOffers,
    visibleCategories: availableCategories,
  } = useBrowsingChannelCatalog();
  const { merchantIsOpen } = useMerchantStatus();
  const { cart } = useOptimisticCart();
  const cartItemIds = useMemo(() => [...new Set(cart.items.map((line) => line.itemOffer.itemId))], [cart.items]);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const itemModalContentRef = useRef<HTMLDivElement>(null);

  const itemsFilter = useAtomValue(searchTermAtom);
  const clearFilter = useSetAtom(closeSearchAtom);
  const { itemOfferId, openItem: handleItemClick, closeItem: handleItemModalClose } = useItemModalParam();
  const navRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  const { categoryInViewId, detectActiveCategory, handleCategoryClick } =
    useCategoryScrollSpy(availableCategories);
  useCategoryDeepLink(availableCategories, handleCategoryClick);

  useEffect(() => {
    function handleScroll() {
      detectActiveCategory();

      const stickyEl = stickyRef.current;
      if (stickyEl) {
        const pinnedTop = parseFloat(getComputedStyle(stickyEl).top) || 0;
        setIsStuck(stickyEl.getBoundingClientRect().top <= pinnedTop + 1);
      }
    }

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [detectActiveCategory]);

  usePageBackground();

  const filteredItemOffers = useMemo(() => {
    if (!itemNames) return visibleItemOffers;
    return filterItemOffersByName(visibleItemOffers, itemNames, itemsFilter);
  }, [visibleItemOffers, itemNames, itemsFilter]);

  return (
    <>
      <HeaderMenu basicInfo={basicInfo} deliveryService={deliveryService} />
      <CouponReveal />
      <SchedulingBanner />
      <StorefrontSections />
      <DaypartSuggestionsRow cartItemIds={cartItemIds} />
      <Surface
        surface="page"
        ref={stickyRef}
        className={cn(
          "sticky top-[calc(var(--active-order-bar-height,0px)+3.5rem)] z-sticky-raised grid grid-cols-1 -mt-1 transition-[padding,box-shadow] duration-base ease-standard",
          isStuck && "pt-4 pb-2 shadow-sticky"
        )}
      >
        <PageContainer paint className="flex max-w-shell items-stretch pt-1">
          {availableCategories.length > 0 && (
            <IconButton
              icon={SquaresFour}
              label="Ver todas as categorias"
              variant="tab"
              tone="neutral"
              shape="square"
              size="icon-sm"
              onClick={() => setCategoryPickerOpen(true)}
              className="mr-2 h-auto self-stretch"
            />
          )}
          <nav ref={navRef} className="flex gap-2 flex-1 min-w-0 overflow-x-auto scroll-smooth">
            {availableCategories.map((c) => (
              <CategoriesNavigation
                category={c}
                categoryInViewId={categoryInViewId}
                onCategoryClick={handleCategoryClick}
                key={c.id}
              />
            ))}
          </nav>
        </PageContainer>
      </Surface>
      <Surface surface="page" className="pt-4">
        {availableCategories.length === 0 && !itemsFilter.length && merchantIsOpen ? (
          <PageContainer>
            <EmptyState
              size="section"
              className="min-h-empty justify-center"
              illustration={<LottieComponent />}
              title="Ainda não temos itens disponíveis para venda"
              description="Estamos preparando nosso cardápio. Volte em breve!"
            />
          </PageContainer>
        ) : itemsFilter.length ? (
          filteredItemOffers.length === 0 ? (
            <PageContainer>
              <SearchEmpty
                className="min-h-empty justify-center"
                title="Nenhum produto encontrado"
                description={`Não encontramos produtos com o termo "${itemsFilter}".`}
                onClear={clearFilter}
              />
            </PageContainer>
          ) : (
            <PageContainer bottom="chrome" className="grid min-h-fill max-w-shell content-start gap-3">
              {filteredItemOffers.map((itemOffer, index) => (
                <ItemOfferCard
                  key={itemOffer.id}
                  itemOffer={itemOffer}
                  status="AVAILABLE"
                  disabled={itemOffer.status === "UNAVAILABLE"}
                  index={index}
                  itemOffers={filteredItemOffers}
                  handleItemClick={handleItemClick}
                  itemId={itemOffer.id}
                />
              ))}
            </PageContainer>
          )
        ) : (
          availableCategories.map((category) => (
            <CategoriesList
              key={category.id}
              category={category}
              isLastItem={
                category.id ===
                availableCategories.filter((c) => categoriesItemOffers[c.id]?.length > 0).slice(-1)[0]?.id
              }
            />
          ))
        )}
      </Surface>
      <FloatingCartButton />
      <PageContainer className="md:hidden">
        <FloatingFullscreenButton />
      </PageContainer>

      <CategoryPickerModal
        open={categoryPickerOpen}
        onOpenChange={setCategoryPickerOpen}
        categories={availableCategories}
        activeCategoryId={categoryInViewId}
        onSelect={handleCategoryClick}
      />

      <ItemModal open={itemOfferId !== ""} onClose={handleItemModalClose} contentRef={itemModalContentRef}>
        <ItemModalContent
          onClose={handleItemModalClose}
          itemOfferId={itemOfferId}
          scrollContainerRef={itemModalContentRef}
        />
      </ItemModal>
    </>
  );
}
